import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { apiService } from '../services/api';
import { PROPERTY_CATEGORIES, getCategoryLabel } from '../config/categories';

const AddProperty = ({ onSuccess }) => {
  const [language, setLanguage] = useState('en'); // 'en' or 'hi'
  const [form, setForm] = useState({
    title: '',
    description: '',
    location: '',
    price: '',
    beds: '',
    baths: '',
    area: '',
    rating: '4.5',
    category: 'rent',
    tags: [],
    images: [],
    ownerName: '',
    ownerPhone: '',
    ownerEmail: '',
  });
  const [preview, setPreview] = useState([]);
  const [tagInput, setTagInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const showAlert = (message, severity = 'success') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateFile = (file, maxSizeMB = 10) => {
    if (!file) {
      showAlert('❌ Please select an image', 'error');
      return false;
    }
    if (file.size > maxSizeMB * 1024 * 1024) {
      showAlert(`❌ File size must be less than ${maxSizeMB}MB`, 'error');
      return false;
    }
    if (!file.type.startsWith('image/')) {
      showAlert('❌ Please select a valid image file', 'error');
      return false;
    }
    return true;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter((file) => validateFile(file));
    if (validFiles.length === 0) return;

    setForm((prev) => ({
      ...prev,
      images: [...prev.images, ...validFiles],
    }));

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemoveImage = (index) => {
    setForm((prev) => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index),
    }));
    setPreview((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput)) {
      setForm((prev) => ({
        ...prev,
        tags: [...prev.tags, tagInput],
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setForm((prev) => ({
      ...prev,
      tags: prev.tags.filter((t) => t !== tag),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.location || !form.price || form.images.length === 0) {
      showAlert('❌ Please fill all required fields and upload images', 'error');
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('location', form.location);
      formData.append('price', form.price);
      formData.append('beds', parseInt(form.beds) || 0);
      formData.append('baths', parseInt(form.baths) || 0);
      formData.append('area', form.area);
      formData.append('rating', parseFloat(form.rating) || 4.5);
      formData.append('category', form.category);
      formData.append('tags', JSON.stringify(form.tags));
      formData.append('ownerName', form.ownerName);
      formData.append('ownerPhone', form.ownerPhone);
      formData.append('ownerEmail', form.ownerEmail);

      form.images.forEach((image) => {
        formData.append('images[]', image);
      });

      const response = await apiService.createPropertyWithImages(formData);
      if (response.success) {
        showAlert('✅ Property added successfully!');
        
        // ✅ Reset form after successful submission
        setForm({
          title: '',
          description: '',
          location: '',
          price: '',
          beds: '',
          baths: '',
          area: '',
          rating: '4.5',
          category: 'rent',
          tags: [],
          images: [],
          ownerName: '',
          ownerPhone: '',
          ownerEmail: '',
        });
        setPreview([]);
        
        // ✅ Call parent callback if provided
        if (onSuccess) onSuccess();
      } else {
        showAlert('❌ Error: ' + (response.message || 'Unknown error'), 'error');
      }
    } catch (error) {
      showAlert('❌ Error: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const labels = {
    en: {
      title: 'Property Title',
      description: 'Description',
      location: 'Location',
      price: 'Price',
      beds: 'Beds',
      baths: 'Baths',
      area: 'Area (sq ft)',
      rating: 'Rating',
      category: 'Category',
      ownerName: 'Owner Name',
      ownerPhone: 'Owner Phone',
      ownerEmail: 'Owner Email',
      submit: '➕ Add Property',
      uploadImages: 'Click to upload property images',
      tags: 'Tags',
      addTag: 'Add Tag',
    },
    hi: {
      title: 'संपत्ति का शीर्षक',
      description: 'विवरण',
      location: 'स्थान',
      price: 'मूल्य',
      beds: 'बेडरूम',
      baths: 'बाथरूम',
      area: 'क्षेत्र (वर्ग फीट)',
      rating: 'रेटिंग',
      category: 'श्रेणी',
      ownerName: 'मालिक का नाम',
      ownerPhone: 'मालिक का फोन',
      ownerEmail: 'मालिक का ईमेल',
      submit: '➕ संपत्ति जोड़ें',
      uploadImages: 'संपत्ति की छवियां अपलोड करने के लिए क्लिक करें',
      tags: 'टैग',
      addTag: 'टैग जोड़ें',
    },
  };

  const t = labels[language];

  return (
    <Box>
      {alert && (
        <Alert severity={alert.severity} onClose={() => setAlert(null)} sx={{ mb: 3 }}>
          {alert.message}
        </Alert>
      )}

      {/* ✅ LANGUAGE TOGGLE */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <ToggleButtonGroup
          value={language}
          exclusive
          onChange={(e, newLang) => newLang && setLanguage(newLang)}
          sx={{ bgcolor: '#f5f5f5' }}
        >
          <ToggleButton value="en" sx={{ fontWeight: language === 'en' ? 700 : 400 }}>
            🇬🇧 English
          </ToggleButton>
          <ToggleButton value="hi" sx={{ fontWeight: language === 'hi' ? 700 : 400 }}>
            🇮🇳 हिन्दी
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField 
              label={t.title} 
              name="title" 
              value={form.title} 
              onChange={handleChange} 
              fullWidth 
              required 
              placeholder={language === 'en' ? 'e.g., Beautiful 3BHK Apartment' : 'उदाहरण: सुंदर 3बीएचके अपार्टमेंट'}
            />
            <TextField 
              label={t.description} 
              name="description" 
              value={form.description} 
              onChange={handleChange} 
              fullWidth 
              required 
              multiline 
              rows={3}
              placeholder={language === 'en' ? 'Detailed description about the property' : 'संपत्ति के बारे में विस्तृत विवरण'}
            />
            <TextField 
              label={t.location} 
              name="location" 
              value={form.location} 
              onChange={handleChange} 
              fullWidth 
              required 
              placeholder={language === 'en' ? 'e.g., Bisauli, Uttar Pradesh' : 'उदाहरण: बिसौली, उत्तर प्रदेश'}
            />

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField 
                  label={t.price} 
                  name="price" 
                  value={form.price} 
                  onChange={handleChange} 
                  fullWidth 
                  required 
                  placeholder={language === 'en' ? 'e.g., 500000' : 'उदाहरण: 500000'}
                  type="number"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField 
                  label={t.beds} 
                  name="beds" 
                  type="number" 
                  value={form.beds} 
                  onChange={handleChange} 
                  fullWidth 
                  placeholder="3"
                />
              </Grid>
              <Grid item xs={3}>
                <TextField 
                  label={t.baths} 
                  name="baths" 
                  type="number" 
                  value={form.baths} 
                  onChange={handleChange} 
                  fullWidth 
                  placeholder="2"
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField 
                  label={t.area} 
                  name="area" 
                  value={form.area} 
                  onChange={handleChange} 
                  fullWidth 
                  placeholder={language === 'en' ? 'e.g., 1500' : 'उदाहरण: 1500'}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField 
                  label={t.rating} 
                  name="rating" 
                  type="number" 
                  value={form.rating} 
                  onChange={handleChange} 
                  fullWidth 
                  inputProps={{ min: 0, max: 5, step: 0.1 }}
                />
              </Grid>
            </Grid>

            {/* ✅ MULTILINGUAL CATEGORY DROPDOWN */}
            <FormControl fullWidth required>
              <InputLabel>{t.category}</InputLabel>
              <Select 
                name="category" 
                value={form.category} 
                onChange={handleChange} 
                label={t.category}
              >
                {PROPERTY_CATEGORIES.map((cat) => (
                  <MenuItem key={cat.value} value={cat.value}>
                    {getCategoryLabel(cat.value, 'property', language)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ bgcolor: '#f5f5f5', p: 2, borderRadius: 2, mt: 1 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#333' }}>
                👤 {language === 'en' ? 'Owner Information' : 'मालिक की जानकारी'}
              </Typography>
              <TextField 
                label={t.ownerName} 
                name="ownerName" 
                value={form.ownerName} 
                onChange={handleChange} 
                fullWidth 
                sx={{ mb: 1.5 }}
                placeholder={language === 'en' ? 'Full name' : 'पूरा नाम'}
              />
              <TextField 
                label={t.ownerPhone} 
                name="ownerPhone" 
                value={form.ownerPhone} 
                onChange={handleChange} 
                fullWidth 
                type="tel"
                sx={{ mb: 1.5 }}
                placeholder="9876543210"
              />
              <TextField 
                label={t.ownerEmail} 
                name="ownerEmail" 
                value={form.ownerEmail} 
                onChange={handleChange} 
                fullWidth 
                type="email"
                placeholder="owner@example.com"
              />
            </Box>

            <Box sx={{ border: '2px dashed #667eea', borderRadius: 2, p: 2, textAlign: 'center', cursor: 'pointer', transition: 'all 0.3s' }}>
              <input type="file" accept="image/*" multiple onChange={handleImagesChange} style={{ display: 'none' }} id="property-images-input" />
              <label htmlFor="property-images-input" style={{ cursor: 'pointer', display: 'block' }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: '#667eea', mb: 1 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{t.uploadImages}</Typography>
                <Typography variant="caption" color="text.secondary">{form.images?.length > 0 ? `${form.images.length} files selected` : 'PNG, JPG, GIF up to 10MB each'}</Typography>
              </label>
            </Box>

            <Grid container spacing={2}>
              {preview.map((prev, index) => (
                <Grid item xs={6} key={index}>
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia component="img" height="150" image={prev} alt={`Preview ${index + 1}`} />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleRemoveImage(index)}
                      sx={{ position: 'absolute', top: 5, right: 5, bgcolor: 'rgba(255,255,255,0.8)' }}
                    >
                      ✕
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>

            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>{t.tags}</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField 
                  size="small" 
                  value={tagInput} 
                  onChange={(e) => setTagInput(e.target.value)} 
                  placeholder={language === 'en' ? 'Add tag...' : 'टैग जोड़ें...'} 
                  onKeyPress={(e) => e.key === 'Enter' && handleAddTag()} 
                  fullWidth 
                />
                <Button variant="outlined" onClick={handleAddTag}>{t.addTag}</Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {form.tags.map((tag) => (
                  <Chip 
                    key={tag} 
                    label={tag} 
                    onDelete={() => handleRemoveTag(tag)} 
                    color="primary" 
                    variant="outlined" 
                  />
                ))}
              </Box>
            </Box>

            <Button type="submit" variant="contained" sx={{ bgcolor: '#667eea', mt: 2 }} disabled={loading} fullWidth>
              {loading ? <CircularProgress size={24} /> : t.submit}
            </Button>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          {preview.length > 0 && (
            <Card sx={{ boxShadow: 2 }}>
              <CardMedia component="img" height="200" image={preview[0]} alt="Main preview" />
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{form.title}</Typography>
                <Typography variant="body2" color="text.secondary">📍 {form.location}</Typography>
                <Typography variant="h6" sx={{ color: '#667eea', fontWeight: 700, mt: 1 }}>₹{form.price ? parseFloat(form.price).toLocaleString('en-IN') : '0'}</Typography>
                
                {(form.beds || form.baths || form.area) && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {form.beds > 0 && `🛏️ ${form.beds} ${language === 'en' ? 'BHK' : 'बीएचके'} | `}
                    {form.baths > 0 && `🚿 ${form.baths} ${language === 'en' ? 'Bath' : 'बाथ'} | `}
                    {form.area && `📐 ${form.area} ${language === 'en' ? 'sq ft' : 'वर्ग फीट'}`}
                  </Typography>
                )}
                
                {form.ownerName && (
                  <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="caption" color="text.secondary">{language === 'en' ? 'Owner' : 'मालिक'}</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{form.ownerName}</Typography>
                    {form.ownerPhone && (
                      <Typography variant="body2" color="primary">📞 {form.ownerPhone}</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default AddProperty;